import React from 'react';
import get from 'lodash/get';
import { Path } from 'app/const/Path';
import { useWindowSize } from 'react-use';
import { css } from 'styled-components/macro';
import { Box, Grid, Hidden } from '@material-ui/core';
// next release of nivo should include type declarations for treemap
// @ts-ignore
import { ResponsiveTreeMapHtml } from '@nivo/treemap';
import { BackBtn } from 'app/components/Charts/treemap/common/backbtn';
import { SideList } from 'app/components/Charts/sunburst/common/sidelist';
import { TreemapTooltip } from 'app/components/Charts/treemap/common/tooltip';
import { SmTooltipContainer } from 'app/components/Charts/common/smTooltipContainer';
import { useHistory } from 'react-router-dom';
import {
  emptyData,
  TreemapProps,
  TreemapVizModel,
} from 'app/components/Charts/treemap/data';
import { NoDataReportedMessage } from '../common/NoDataReportedMessage';

const containercss = (dataless: boolean) => css`
  height: 500px;
  font-size: 22px;
  position: relative;
  :hover {
    cursor: ${dataless ? 'unset' : 'pointer'};
  }

  * {
    cursor: ${dataless ? 'unset' : 'pointer'} !important;
  }

  span {
    display: ${dataless ? 'none' : 'unset'};
  }

  ::after {
    content: 'Not available';
    display: ${dataless ? 'block' : 'none'};
    width: 100%;
    text-align: center;
    position: absolute;
    top: calc(50% - 11px);
    font-weight: bold;
    font-size: 16.015px;
    color: rgba(61, 61, 61, 0.6);
  }
`;

const smspacercss = css`
  width: 100%;
  height: 55px;
`;

export function TreemapChart(props: TreemapProps) {
  const { width } = useWindowSize();
  const [drilldownId, setDrilldownId] = React.useState(null);
  const [renderedNodes, setRenderedNodes] = React.useState({ ...props.data });
  const [smTooltip, setSmTooltip] = React.useState(null);
  const history = useHistory();

  const showStandardTooltip =
    !('ontouchstart' in document.documentElement) || width > 767;

  function onNodeClick(node: any, e: any, doSmDrilldown = false) {
    if (showStandardTooltip || doSmDrilldown) {
      const children = get(node.data, 'orgs', []);
      if (children.length > 0) {
        setRenderedNodes({
          ...renderedNodes,
          children: children.map((child: any) => ({
            name: child.title,
            value: child.size,
            color: child.color,
            ref: child.ref,
          })),
        });
        setDrilldownId(node.data.id);
      } else {
        navigateToDetailPage(node);
      }
    } else {
      setSmTooltip(node);
    }
  }

  function navigateToDetailPage(node: any) {
    const detailPage = props.label;
    if (node.data.ref) {
      if (detailPage === 'Publishers') {
        history.push(
          `${Path.detail.publisher.replace(':param', node.data.ref)}/projects?${
            props.detailBaseFilter
          }=${props.pageParam}`
        );
      }
      if (detailPage === 'Donors') {
        history.push(
          `${Path.detail.donor.replace(':param', node.data.ref)}/projects?${
            props.detailBaseFilter
          }=${props.pageParam}`
        );
      }
      if (detailPage === 'Organisations') {
        history.push(
          `${Path.detail.organisation.replace(
            ':param',
            node.data.ref
          )}/projects?${props.detailBaseFilter}=${props.pageParam}`
        );
      }
    }
  }

  function goBack(event: React.MouseEvent<HTMLButtonElement>) {
    setDrilldownId(null);
    setRenderedNodes({ ...props.data });
  }

  React.useEffect(() => {
    if (props.data.children.length > 0) {
      setRenderedNodes({ ...props.data });
    } else {
      setRenderedNodes({ ...emptyData });
    }
  }, [props.data]);

  return (
    <Grid container>
      <Box height="40px" width="100%" />

      <Grid
        item
        md={12}
        lg={3}
        css={`
          @media (max-width: 960px) {
            order: 2;
          }
        `}
      >
        <SideList
          withLinks
          selected=""
          overflowList
          type={props.label}
          handleClick={onNodeClick}
          data={{
            ...renderedNodes,
            children: renderedNodes.children.map((item: any) => ({
              ...item,
              title: item.name,
              id: item.name,
            })),
          }}
          title={drilldownId || props.label}
        />
      </Grid>
      <Hidden smUp>
        <div css={smspacercss} />
      </Hidden>
      <Grid
        item
        md={12}
        lg={9}
        css={`
          position: relative;
          @media (min-width: 960px) {
            transform: translateX(8px);
          }
          @media (max-width: 960px) {
            order: 1;
            margin-bottom: 24px;
            flex-grow: 0;
            max-width: 100%;
            flex-basis: 100%;
          }
        `}
      >
        <div css={containercss(renderedNodes.name === 'empty')}>
          {drilldownId && <BackBtn onClick={goBack} />}
          <ResponsiveTreeMapHtml
            root={renderedNodes}
            isInteractive={renderedNodes.name !== 'empty'}
            onClick={onNodeClick}
            // @ts-ignore
            tooltip={!showStandardTooltip ? () => null : TreemapTooltip}
            {...TreemapVizModel}
            theme={{
              ...TreemapVizModel.theme,
              tooltip: {
                ...TreemapVizModel.theme.tooltip,
                container: {
                  ...TreemapVizModel.theme.tooltip.container,
                  display: showStandardTooltip ? 'inherit' : 'none',
                },
              },
            }}
          />
          {renderedNodes.name === 'empty' && <NoDataReportedMessage />}
        </div>
      </Grid>
      {!showStandardTooltip && smTooltip && (
        <SmTooltipContainer
          showDrilldownBtn={!drilldownId}
          close={() => setSmTooltip(null)}
          drilldown={() => onNodeClick(smTooltip, null, true)}
        >
          <TreemapTooltip node={smTooltip} showAll />
        </SmTooltipContainer>
      )}
    </Grid>
  );
}
